import { useCallback, useEffect, useRef, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import { ImageUp, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getCroppedPreviewUrl, type PixelCrop } from '@/lib/crop-image';
import { cn } from '@/lib/utils';

// Valor pendiente de subir: el original + la región de recorte (en
// píxeles de la imagen original). La subida real ocurre al guardar la
// colección (necesita el id).
export interface PendingImage {
  file: File;
  crop: PixelCrop;
}

interface CollectionImageFieldProps {
  title: string;
  hint: string;
  /** Aspect ratio bloqueado en el cropper (ancho/alto). */
  aspect: number;
  /** URL de la imagen ya subida (modo edición), si existe. */
  existingUrl?: string;
  /** Reporta el recorte pendiente al formulario (o null si se quita). */
  onChange(pending: PendingImage | null): void;
}

/**
 * Gestor de una imagen: drag&drop / selección, editor de recorte con
 * ratio bloqueado (react-easy-crop) y previsualización. No sube nada
 * por sí mismo: entrega el `PendingImage` al formulario, que lo sube
 * tras crear/guardar la colección.
 */
export function CollectionImageField({
  title,
  hint,
  aspect,
  existingUrl,
  onChange,
}: CollectionImageFieldProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);

  // Edición de recorte.
  const [editorSrc, setEditorSrc] = useState<string | null>(null);
  const [editorFile, setEditorFile] = useState<File | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState<Area | null>(null);

  // Previsualización del recorte confirmado (object URL local).
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Limpia object URLs al desmontar / cambiar.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const openEditor = useCallback((file: File) => {
    const src = URL.createObjectURL(file);
    setEditorFile(file);
    setEditorSrc(src);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setAreaPixels(null);
  }, []);

  const handleFiles = useCallback(
    (files: FileList | null) => {
      const file = files?.[0];
      if (file && file.type.startsWith('image/')) openEditor(file);
    },
    [openEditor],
  );

  const confirmCrop = useCallback(async () => {
    if (!editorFile || !editorSrc || !areaPixels) return;
    const cropPx: PixelCrop = {
      x: areaPixels.x,
      y: areaPixels.y,
      width: areaPixels.width,
      height: areaPixels.height,
    };
    const url = await getCroppedPreviewUrl(editorSrc, cropPx);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return url;
    });
    onChange({ file: editorFile, crop: cropPx });
    // Cierra el editor (libera el src del original).
    URL.revokeObjectURL(editorSrc);
    setEditorSrc(null);
    setEditorFile(null);
  }, [editorFile, editorSrc, areaPixels, onChange]);

  const cancelEditor = useCallback(() => {
    if (editorSrc) URL.revokeObjectURL(editorSrc);
    setEditorSrc(null);
    setEditorFile(null);
  }, [editorSrc]);

  const clearImage = useCallback(() => {
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    onChange(null);
  }, [onChange]);

  const shownUrl = previewUrl ?? existingUrl ?? null;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium">{title}</h4>
        {shownUrl && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => inputRef.current?.click()}
          >
            Cambiar
          </Button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = '';
        }}
      />

      {shownUrl ? (
        <div className="relative overflow-hidden rounded-lg border bg-muted" style={{ aspectRatio: aspect }}>
          <img src={shownUrl} alt="" className="size-full object-cover" />
          <Button
            type="button"
            variant="secondary"
            size="icon"
            aria-label="Quitar imagen"
            onClick={clearImage}
            className="absolute right-2 top-2 size-7"
          >
            <X className="size-4" aria-hidden />
          </Button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFiles(e.dataTransfer.files);
          }}
          className={cn(
            'flex w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground transition-colors',
            dragOver ? 'border-foreground bg-muted' : 'hover:bg-muted/50',
          )}
          style={{ aspectRatio: aspect }}
        >
          <ImageUp className="size-6" aria-hidden />
          <span>Arrastra una imagen o haz clic para subir</span>
          <span className="text-xs text-muted-foreground/70">{hint}</span>
        </button>
      )}

      {/* Editor de recorte */}
      <Dialog open={Boolean(editorSrc)} onOpenChange={(o) => !o && cancelEditor()}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Recortar imagen — {title}</DialogTitle>
          </DialogHeader>
          <div className="relative h-[360px] w-full overflow-hidden rounded-md bg-black">
            {editorSrc && (
              <Cropper
                image={editorSrc}
                crop={crop}
                zoom={zoom}
                aspect={aspect}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_area, areaPx) => setAreaPixels(areaPx)}
              />
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">Zoom</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1"
              aria-label="Zoom"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={cancelEditor}>
              Cancelar
            </Button>
            <Button type="button" onClick={confirmCrop} disabled={!areaPixels}>
              Aplicar recorte
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
